import React, { useContext, useEffect, useRef, useState } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import Loader from '../components/Loader/Loader';
import CulminacionLaboralService from '../axios_services/culminacionLaboral.service';
import { ButtonInsert, ButtonSearch, ButtonUpdate } from '../components/Buttons/Buttons';
import ModalCulminacionLaboralForm from '../components/Modal/ModalCulminacionLaboralForm';
import ModalCulminacionLaboralArchivo from '../components/Modal/ModalCulminacionLaboralArchivo';
import { FileText } from 'lucide-react';
import './Induccion.css';

function CulminacionVinculoLaboral() {
  const [state] = useContext(AppContext);
  const perfilId = Number(state.user?.perfil_id);
  const esTH = [5, 8, 22, 26].includes(perfilId);

  const [registros, setRegistros] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filtroPeriodo, setFiltroPeriodo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [catalogo, setCatalogo] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [modalForm, setModalForm] = useState({ isOpen: false, row: null });
  const [modalArchivo, setModalArchivo] = useState({ isOpen: false, row: null, campo: null });

  const initialFetchDone = useRef(false);

  const loadCatalogoYUsuarios = async () => {
    const [cat, usr] = await Promise.all([
      CulminacionLaboralService.catalogo(),
      CulminacionLaboralService.usuariosOpciones(),
    ]);
    if (cat.success) setCatalogo(cat.data);
    if (usr.success) setUsuarios(usr.data || []);
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filtroPeriodo) params.periodo = filtroPeriodo;
      const resp = await CulminacionLaboralService.listar(params);
      setRegistros(resp.data || []);
    } catch (error) {
      setRegistros([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      (async () => {
        try {
          await loadCatalogoYUsuarios();
        } catch (e) {
          /* ignore */
        }
        await loadData();
      })();
    }
  }, []);

  const formatFecha = (f) => {
    if (!f) return '-';
    return String(f).split('T')[0];
  };

  const formatFechaHora = (f) => {
    if (!f) return '-';
    const s = String(f);
    return s.includes('T') ? `${s.split('T')[0]} ${s.split('T')[1]?.slice(0, 5) || ''}` : s;
  };

  const renderDocCell = (r, campo, urlPublica, tieneFlag, titulo) => {
    const tiene = Number(tieneFlag) === 1 && urlPublica;
    if (tiene) {
      return (
        <button
          type="button"
          title={`Ver ${titulo}`}
          className="acta-icon-doc acta-icon-doc--ok"
          onClick={() => window.open(urlPublica, '_blank')}
        >
          <FileText size={18} strokeWidth={2} aria-hidden />
        </button>
      );
    }
    return (
      <button
        type="button"
        title={`Subir ${titulo} (PDF)`}
        className="acta-icon-doc acta-icon-doc--falta"
        onClick={() => setModalArchivo({ isOpen: true, row: r, campo })}
      >
        <FileText size={18} strokeWidth={2} aria-hidden />
      </button>
    );
  };

  const th = (min) => ({ minWidth: min, whiteSpace: 'nowrap' });

  if (!esTH) return <div className="caja-flujo-container">No tiene permisos para acceder.</div>;

  if (isLoading && registros.length === 0) return <Loader />;

  return (
    <div className="caja-flujo-container induccion-modulo">
      <h1>REGISTRO DE RENUNCIAS</h1>

      <div className="filtros induccion-filtros">
        <div className="campo-fecha">
          <label>Periodo:</label>
          <input type="month" value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.target.value)} />
        </div>
      </div>

      <div className="filtros-actions">
        <ButtonSearch onClick={loadData} isLoading={isLoading} />
      </div>

      <div className="nuevo-movimiento-section">
        <ButtonInsert onClick={() => setModalForm({ isOpen: true, row: null })} />
      </div>

      <div className="table-container" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="induccion-table" style={{ minWidth: '1800px' }}>
          <thead>
            <tr>
              <th style={th('200px')}>Colaborador</th>
              <th style={th('220px')}>Estado</th>
              <th style={th('150px')}>Fecha inicio</th>
              <th style={th('120px')}>Fecha fin</th>
              <th style={th('200px')}>Registrado por</th>
              <th style={{ minWidth: '200px', maxWidth: '280px' }}>Observación</th>
              <th style={th('140px')}>Renuncia</th>
              <th style={th('120px')}>Planilla</th>
              <th style={th('160px')}>Entrega de cargo</th>
              <th style={th('140px')}>Boleta de pago</th>
              <th style={th('130px')}>Certificado</th>
              <th style={th('110px')}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {registros.length > 0 ? (
              registros.map((r) => (
                <tr key={r.id}>
                  <td>{r.nombre_usuario || '-'}</td>
                  <td>{r.estado_descripcion || r.estado}</td>
                  <td>{formatFechaHora(r.fecha_inicio)}</td>
                  <td>{formatFecha(r.fecha_fin)}</td>
                  <td>{r.nombre_registra || '-'}</td>
                  <td style={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>{r.observacion || '-'}</td>
                  <td>
                    <div className="acta-cell">
                      {renderDocCell(r, 'renuncia', r.url_renuncia_publica, r.tiene_renuncia, 'renuncia')}
                    </div>
                  </td>
                  <td>
                    <div className="acta-cell">
                      {renderDocCell(r, 'planilla', r.url_planilla_publica, r.tiene_planilla, 'planilla')}
                    </div>
                  </td>
                  <td>
                    <div className="acta-cell">
                      {renderDocCell(r, 'entrega_cargo', r.url_entrega_cargo_publica, r.tiene_entrega_cargo, 'entrega de cargo')}
                    </div>
                  </td>
                  <td>
                    <div className="acta-cell">
                      {renderDocCell(r, 'boleta_pago', r.url_boleta_pago_publica, r.tiene_boleta_pago, 'boleta de pago')}
                    </div>
                  </td>
                  <td>
                    <div className="acta-cell">
                      {renderDocCell(r, 'certificado', r.url_certificado_publica, r.tiene_certificado, 'certificado')}
                    </div>
                  </td>
                  <td>
                    <div className="induccion-acciones">
                      <ButtonUpdate title="Editar" onClick={() => setModalForm({ isOpen: true, row: r })} />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={12} className="sin-data">
                  No hay registros.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <ModalCulminacionLaboralForm
        isOpen={modalForm.isOpen}
        row={modalForm.row}
        usuarios={usuarios}
        estados={catalogo?.estados || []}
        onClose={() => setModalForm({ isOpen: false, row: null })}
        onSave={async (payload) => {
          if (modalForm.row) {
            await CulminacionLaboralService.actualizar(payload);
          } else {
            await CulminacionLaboralService.crear(payload);
          }
          await loadData();
        }}
      />

      <ModalCulminacionLaboralArchivo
        isOpen={modalArchivo.isOpen}
        row={modalArchivo.row}
        campo={modalArchivo.campo}
        onClose={() => setModalArchivo({ isOpen: false, row: null, campo: null })}
        onSave={async ({ id, campo, archivo }) => {
          setIsLoading(true);
          try {
            await CulminacionLaboralService.subirArchivo(id, campo, archivo, state.user?.usuario_id);
            await loadData();
          } finally {
            setIsLoading(false);
          }
        }}
      />
    </div>
  );
}

export default CulminacionVinculoLaboral;
